import {
  CardContent,
  LoaderCircleV2,
  PaddingBottom20,
  UI_BODY_SM,
  colors2,
} from "oolib";
import React from "react";
import { useGetDataLazy } from "../../utils/react-query-hooks/getData";
import { useElemInView } from "../../utils/customHooks/useElemInView";
import { __GetContentTypeConfigNew } from "../../utils/getters/gettersV2";

/**
 * Tab content for a content type configured with `allowMultipleSubmissions: true`.
 *
 * Unlike the single-submission flow (which tracks one doc per content type on
 * `user.selfServeSurveys`), this lists the user's OWN documents for this content
 * type straight from the content collection (filtered by `meta.kp_contributed_by`,
 * exactly like the "My Content" page). No per-user array bookkeeping, so the user
 * doc never bloats, and "+ New submission" is just a fresh create-draft flow.
 */
const cardConfig = {
  title: "title",
  cardLabel: "Submission",
  tagDisplay: "status",
  desc: "description",
};

const statusDisplayMap = {
  draft: "Draft",
  // a published doc that's currently being edited (an edit-clone exists) — NOT a draft
  editPublished: "Published: Under Edit",
  published: "Published",
  awaitingModeration: "In Review",
  underModeration: "In Review",
  sentBack: "Sent Back",
};

const MultiSubmissionTab = ({ contentType, userId, onOpenSubmission }) => {
  // the user's own docs for this single content type, drafts + published.
  // plain per-tab listing query (one content type), filtered by contributor.
  // NOTE: no kp_published_status restriction (unlike the listing page's
  // fixedGetDataConfig) so participants see their own drafts too.
  const { data, status, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetDataLazy({
      contentTypes: [contentType],
      // `editPublishedDocId: { $exists: false }` hides the ORIGINAL published doc
      // while it's being edited (the original gets editPublishedDocId set, the
      // edit-clone doesn't) — so we show one card per submission: the working copy.
      // Same approach as the My Content page.
      findQuery: {
        "meta.kp_contributed_by": userId,
        editPublishedDocId: { $exists: false },
      },
      projection: {
        "main.title": 1,
        kp_published_status: 1,
        "meta.kp_content_type": 1,
        // needed to resume editing an editPublished clone: getContentForEdit keys
        // off the OG published doc id, which the clone stores here.
        kp_og_published_doc: 1,
      },
      collectionId: `selfServeSubmissions::${contentType}`,
    });

  const submissions = data
    ? data.pages.flatMap((page) => page.data || [])
    : [];

  // infinite scroll: load the next page when the last card scrolls into view
  // (same pattern as the listing page / annotation explorer).
  const { observerRef } = useElemInView({
    callback: fetchNextPage,
    enabled: !!hasNextPage && !isFetchingNextPage,
  });

  const contentTypeTitle =
    __GetContentTypeConfigNew(contentType)?.general?.content?.title ||
    contentType;

  const getCardData = (doc) => ({
    _id: doc._id,
    contentType,
    kp_published_status: doc.kp_published_status,
    kp_og_published_doc: doc.kp_og_published_doc,
    status: statusDisplayMap[doc.kp_published_status] || doc.kp_published_status,
    title: doc.main?.title || contentTypeTitle,
    description: "",
  });

  return (
    <div>
      {status === "success" && submissions.length === 0 && (
        <div style={{ textAlign: "center", padding: "2rem 0" }}>
          <UI_BODY_SM color={colors2.grey60}>
            No submissions yet. Click "New submission" to get started.
          </UI_BODY_SM>
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        {submissions.map((doc, index) => (
          // observerRef sits on the last card; when it scrolls into view the next
          // page loads. The id gives useElemInView a stable per-row de-bounce key.
          <div
            key={doc._id}
            id={`submission-row-${doc._id}`}
            ref={index === submissions.length - 1 ? observerRef : undefined}
          >
            <CardContent
              id={`submission-${doc._id}`}
              data={getCardData(doc)}
              config={cardConfig}
              onClick={(e, d) => onOpenSubmission(d)}
              statusTagVariant={
                doc.kp_published_status === "published" ? "positive" : "neutral"
              }
            />
          </div>
        ))}
      </div>

      {isFetchingNextPage && (
        <div style={{ display: "flex", justifyContent: "center", padding: "1rem 0" }}>
          <LoaderCircleV2 S />
        </div>
      )}
      <PaddingBottom20 />
    </div>
  );
};

export default MultiSubmissionTab;
